import React, { useCallback, useState, FC } from 'react';
import { Modal, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles, Theme } from '@material-ui/core/styles';

type Props = {
  className?: string;
};

type ModalType = {
  Modal: React.FC<Props>;
  open: () => void;
  close: () => void;
  isOpen: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  modalRoot: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateY(-50%) translateX(-50%)',
    '-webkit-transform': 'translateY(-50%) translateX(-50%)',
    minWidth: 320,
    minHeight: 120,
    background: theme.palette.background.default,
    borderRadius: 5,
    textAlign: 'center',
    padding: theme.spacing(2),
  },

  close: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
}));

export const useModal = (
  defaultOpen = true,
  closed?: () => void,
): ModalType => {
  const classes = useStyles();

  const [isOpen, setOpen] = useState(defaultOpen);

  const open = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const close = useCallback(() => {
    setOpen(false);

    if (closed) {
      closed();
    }
  }, [setOpen, closed]);

  const ModalWrapper: FC<Props> = useCallback(
    ({ children, className }) => {
      if (!isOpen) return <></>;

      return (
        <Modal open={isOpen} onClose={close}>
          <div className={`${classes.modalRoot} ${className}`}>
            <IconButton className={classes.close} onClick={close}>
              <CloseIcon />
            </IconButton>
            <>{children}</>
          </div>
        </Modal>
      );
    },
    [isOpen, close, classes.modalRoot, classes.close],
  );

  return { Modal: ModalWrapper, open, close, isOpen };
};
